import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { Stack } from '@astryxdesign/core/Stack'
import { Card } from '@astryxdesign/core/Card'
import { Text } from '@astryxdesign/core/Text'
import { Button } from '@astryxdesign/core/Button'
import { Badge } from '@astryxdesign/core/Badge'
import { List, ListItem } from '@astryxdesign/core/List'
import { SelectableCard } from '@astryxdesign/core/SelectableCard'
import { Divider } from '@astryxdesign/core/Divider'
import { ActivityDocs, ActivityMeta, priceOf, timeOf } from '#/components/ActivityRow'
import { enqueue } from '#/lib/sync'
import type { Item } from '#/lib/types'
import { useBookings, useDayItems, useDays, useExpenses, useMe, usePlaces, useTrip } from '#/lib/hooks'
import { formatMinor } from '#/lib/money'

export const Route = createFileRoute('/')({ component: Today })

/*
  The default route, and for most of the trip the only screen anyone opens.
  Where am I, what is next, what have we spent.
*/
function Today() {
  const trip = useTrip()
  const places = usePlaces(trip?.id)
  const bookings = useBookings(trip?.id)
  const expenses = useExpenses(trip?.id)
  const me = useMe()
  const today = new Date().toISOString().slice(0, 10)
  const days = useDays(trip?.id)

  // Before the trip starts — and on any gap day during it — "today" has nothing
  // on it. Showing an empty state for thirteen days makes the screen useless
  // exactly when you are planning. Fall forward to the next day that has a
  // plan, and say plainly that is what you are looking at.
  const focusDay = days.includes(today)
    ? today
    : days.find((d) => d >= today) ?? days[days.length - 1] ?? today
  const isToday = focusDay === today
  const daysAway = Math.round(
    (new Date(`${focusDay}T00:00:00`).getTime() - new Date(`${today}T00:00:00`).getTime()) / 864e5,
  )
  const focusItems = useDayItems(trip?.id, focusDay)

  if (!trip) {
    return <Text type="supporting">No trip yet. Seed one, or add it in Settings.</Text>
  }

  const here =
    places.find((p) => p.arrive_date <= focusDay && p.depart_date >= focusDay) ?? places[0]

  const now = Date.now()
  const nextBooking = bookings.find((b) => (b.starts_at ?? 0) > now && b.status === 'booked')

  const startOfDay = new Date(); startOfDay.setHours(0, 0, 0, 0)
  const todaySpend = expenses
    .filter((e) => e.spent_at >= startOfDay.getTime())
    .reduce((s, e) => s + e.amount_minor, 0)
  const tripSpend = expenses.reduce((s, e) => s + e.amount_minor, 0)

  return (
    <Stack direction="vertical" gap={5}>
      <Stack direction="vertical" gap={0}>
        <Text type="supporting">{trip.name}</Text>
        <Text type="display-3" display="block">{here?.name ?? 'Not started'}</Text>
        {here && (
          <Text type="supporting">
            {fmtDate(here.arrive_date)} – {fmtDate(here.depart_date)}
          </Text>
        )}
      </Stack>

      {nextBooking && <NextBooking booking={nextBooking} />}

      <TodayChecklist
        items={focusItems}
        meId={me?.id}
        isToday={isToday}
        daysAway={daysAway}
        dayNumber={days.indexOf(focusDay) + 1}
        dayLabel={fmtDate(focusDay)}
      />

      <Card>
        <Stack direction="horizontal" justify="between" align="end" gap={4}>
          <Stack direction="vertical" gap={0}>
            <Text type="supporting">Today</Text>
            <Text type="display-3" hasTabularNumbers display="block">
              {formatMinor(todaySpend, trip.base_currency)}
            </Text>
          </Stack>
          <Stack direction="vertical" gap={0} align="end">
            <Text type="supporting">Trip</Text>
            <Text type="large" hasTabularNumbers display="block">
              {formatMinor(tripSpend, trip.base_currency)}
            </Text>
          </Stack>
        </Stack>
      </Card>

      {expenses.length > 0 && (
        <Stack direction="vertical" gap={2}>
          <Text type="label" color="secondary">Recent</Text>
          <List hasDividers density="compact">
            {expenses.slice(0, 4).map((e) => (
              <ListItem
                key={e.id}
                label={e.note || e.category}
                endContent={
                  <Text hasTabularNumbers>{formatMinor(e.amount_minor, e.currency)}</Text>
                }
              />
            ))}
          </List>
        </Stack>
      )}
    </Stack>
  )
}

/**
 * The day as a checklist. Ticking is shared — four people on one trip work one
 * list, so the state lives on the row rather than per user. Each line carries
 * what you actually need standing on a street: when, what it costs, how long
 * the leg from the last stop takes, and whether a ticket is still unbought.
 */
function TodayChecklist({
  items, meId, isToday, daysAway, dayNumber, dayLabel,
}: {
  items: Item[]
  meId?: string
  isToday: boolean
  daysAway: number
  dayNumber: number
  dayLabel: string
}) {
  if (!items.length) {
    return (
      <Stack direction="vertical" gap={2}>
        <Text type="label" color="secondary">Today</Text>
        <Text type="supporting">Nothing planned yet.</Text>
      </Stack>
    )
  }

  const heading = isToday
    ? 'Today'
    : daysAway === 1
      ? `Tomorrow · Day ${dayNumber}`
      : `Day ${dayNumber} · ${dayLabel}`

  const done = items.filter((i) => i.status === 'done')
  const estimate = items.reduce((s, i) => s + (i.price_minor ?? 0), 0)
  const legs = items.reduce((s, i) => s + (i.travel_min ?? 0), 0)

  // The write lands in IndexedDB first, so the card flips before the request
  // leaves the phone.
  async function toggle(id: string, done: boolean) {
    const item = items.find((i) => i.id === id)
    if (!item) return
    await enqueue({
      table: 'items',
      op: 'upsert',
      row: {
        ...item,
        status: done ? 'done' : 'planned',
        completed_at: done ? Date.now() : null,
        completed_by: done ? meId ?? null : null,
      },
    })
  }

  return (
    <Stack direction="vertical" gap={2}>
      <Stack direction="horizontal" justify="between" align="center" gap={2}>
        <Text type="label" color="secondary">{heading}</Text>
        <Text type="supporting" hasTabularNumbers>
          {isToday ? `${done.length}/${items.length} done` : `in ${daysAway} days`}
        </Text>
      </Stack>

      {/* One card per activity rather than rows in a list. Run together, the
          end of one time block and the start of the next were indistinguishable;
          a card gives each its own edge, and the whole card is the tap target
          rather than a 20px checkbox. */}
      <Stack direction="vertical" gap={2}>
        {items.map((i) => {
          const isDone = i.status === 'done'
          return (
            <SelectableCard
              key={i.id}
              label={`${timeOf(i)} ${i.title}`}
              isSelected={isDone}
              onChange={(next) => toggle(i.id, next)}
              variant={isDone ? 'muted' : 'default'}
              padding={4}
            >
              <Stack direction="vertical" gap={2}>
                <Stack direction="horizontal" justify="between" align="center" gap={2}>
                  <Text type="supporting" hasTabularNumbers>{timeOf(i)}</Text>
                  {isDone && <Badge label="Done" variant="green" />}
                  {!isDone && i.is_key ? <Badge label="Key" variant="purple" /> : null}
                </Stack>

                <Text
                  weight={i.is_key ? 'semibold' : 'medium'}
                  hasStrikethrough={isDone}
                  display="block"
                >
                  {i.title}
                </Text>

                {i.short_description && (
                  <Text type="supporting">{i.short_description}</Text>
                )}

                <ActivityMeta item={i} />

                {i.how_to_reach && <Text type="supporting">{i.how_to_reach}</Text>}
                <ActivityDocs item={i} />
              </Stack>
            </SelectableCard>
          )
        })}
      </Stack>

      <Divider />
      <Stack direction="horizontal" justify="between" gap={3} wrap="wrap">
        <Text type="supporting" hasTabularNumbers>
          {estimate > 0 ? `~${priceOf({ ...items[0], price_minor: estimate, price_basis: 'total' })} planned` : 'Nothing to pay for'}
        </Text>
        {legs > 0 && <Text type="supporting">{legs} min moving</Text>}
      </Stack>
    </Stack>
  )
}

/* The feature that earns the whole project at a station counter. */
function NextBooking({ booking }: { booking: ReturnType<typeof useBookings>[number] }) {
  const [copied, setCopied] = useState(false)

  async function copy() {
    if (!booking.ref) return
    try {
      await navigator.clipboard.writeText(booking.ref)
      setCopied(true)
      setTimeout(() => setCopied(false), 1600)
    } catch { /* clipboard blocked; the reference is on screen anyway */ }
  }

  return (
    <Card variant="muted">
      <Stack direction="vertical" gap={2}>
        <Stack direction="horizontal" justify="between" align="center" gap={2}>
          <Text type="label" color="secondary">Next</Text>
          <Badge label={booking.kind} variant="neutral" />
        </Stack>
        <Text weight="medium">
          {booking.from_loc && booking.to_loc
            ? `${booking.from_loc} → ${booking.to_loc}`
            : booking.vendor ?? booking.kind}
        </Text>
        <Text type="supporting">
          {booking.starts_at
            ? `${fmtDate(new Date(booking.starts_at).toISOString().slice(0, 10))} · ${fmtTime(booking.starts_at)}`
            : 'Time to be confirmed'}
        </Text>
        {booking.ref && (
          <Button
            label={copied ? 'Copied' : booking.ref}
            variant="secondary"
            width="100%"
            onClick={copy}
          />
        )}
      </Stack>
    </Card>
  )
}

const fmtDate = (iso: string) => {
  const d = new Date(`${iso}T00:00:00`)
  return Number.isNaN(d.getTime()) ? iso
    : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
}
const fmtTime = (ms: number) =>
  new Date(ms).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' })
